import {
  DataTable,
  CheckboxCell,
  CheckboxHeader,
} from '@/components/table/DataTable';
import { TanstackSortableColumnHeader } from '@/components/table/SortColumnHeader';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Text } from '@/components/ui/text';
import {
  useBackOfficeWorkspacesService,
  useDeleteWorkspaceService,
  useAdminUpdateWorkspaceService,
} from '@/services/backoffice';
import type { BackOfficeWorkspace, PlanTier } from '@ditto/shared';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { getSortedRowModel } from '@tanstack/react-table';
import { MoreHorizontal, Pencil, Search, Trash2 } from 'lucide-react';
import { useMemo, useState, type FC } from 'react';
import { CreateWorkspaceModal } from '../../components/CreateWorkspaceModal';
import { WorkspaceEditModal } from '../../components/WorkspaceUsersModal';
import { WorkspacesActionBar } from './components/WorkspacesActionBar';

const TIER_LABELS: Record<PlanTier, string> = {
  free: 'Free',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

export const AdminWorkspacesPage: FC = () => {
  const { workspaces, isLoading: workspacesLoading } =
    useBackOfficeWorkspacesService();
  const [createWorkspaceModalOpen, setCreateWorkspaceModalOpen] =
    useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<any>(null);
  const [workspaceSearchQuery, setWorkspaceSearchQuery] = useState('');
  const { deleteWorkspace, isPending: isDeleting } =
    useDeleteWorkspaceService();
  const { updateWorkspace, isPending: isUpdating } =
    useAdminUpdateWorkspaceService();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const handleTierChange = (workspaceId: string, tier: PlanTier) => {
    updateWorkspace({ workspaceId, tier });
  };

  const filteredWorkspaces = useMemo(() => {
    if (!workspaceSearchQuery.trim()) return workspaces;
    return workspaces.filter(workspace =>
      `${workspace.name} ${workspace.uname}`
        .toLowerCase()
        .includes(workspaceSearchQuery.toLowerCase())
    );
  }, [workspaces, workspaceSearchQuery]);

  const selectedWorkspaces = useMemo(() => {
    return filteredWorkspaces.filter(w => rowSelection[w.id]);
  }, [filteredWorkspaces, rowSelection]);

  const clearSelection = () => {
    setRowSelection({});
  };

  const columns = useMemo<ColumnDef<BackOfficeWorkspace>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => <CheckboxHeader table={table} />,
        cell: ({ row }) => <CheckboxCell row={row} />,
        meta: { headerClassName: 'w-[50px]' },
      },
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            <Ellipsis asChild lineClamp={1} hasTooltip>
              <Text variant="body-sm" color="primary" className="font-medium">
                Name
              </Text>
            </Ellipsis>
          </TanstackSortableColumnHeader>
        ),
      },
      {
        accessorKey: 'uname',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            <Ellipsis asChild lineClamp={1} hasTooltip>
              <Text variant="body-sm" color="primary" className="font-medium">
                Uname
              </Text>
            </Ellipsis>
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => (
          <span className="font-mono text-sm">{row.original.uname}</span>
        ),
      },
      {
        accessorKey: 'memberCount',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            <Ellipsis asChild lineClamp={1} hasTooltip>
              <Text variant="body-sm" color="primary" className="font-medium">
                Users
              </Text>
            </Ellipsis>
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => (
          <span
            className={
              row.original.memberCount === 0 ? 'text-red-500 font-medium' : ''
            }
          >
            {row.original.memberCount}
          </span>
        ),
      },
      {
        id: 'tier',
        header: 'Tier',
        cell: ({ row }) => (
          <Select
            value={row.original.tier}
            onValueChange={(value: PlanTier) =>
              handleTierChange(row.original.id, value)
            }
            disabled={isUpdating}
          >
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(TIER_LABELS) as [PlanTier, string][]).map(
                ([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        ),
      },
      {
        id: 'actions',
        meta: { headerClassName: 'w-[50px]' },
        cell: ({ row }) => {
          const workspace = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={e => e.stopPropagation()}
                >
                  <MoreHorizontal size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedWorkspace(workspace);
                    setEditModalOpen(true);
                  }}
                >
                  <Pencil size={12} className="mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  disabled={isDeleting}
                  onClick={e => {
                    e.stopPropagation();
                    if (
                      window.confirm(
                        `Delete "${workspace.name}"? This cannot be undone.`
                      )
                    ) {
                      deleteWorkspace(workspace.id);
                    }
                  }}
                >
                  <Trash2 size={12} className="mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [isUpdating, isDeleting, handleTierChange, deleteWorkspace]
  );

  return (
    <FlexCol gap="6" w="full" h="full" p="6" className="max-w-[1600px]">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">All Workspaces</h2>
        <Button onClick={() => setCreateWorkspaceModalOpen(true)}>
          Create Workspace
        </Button>
      </div>
      <div className="relative max-w-sm">
        <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search workspaces..."
          value={workspaceSearchQuery}
          onChange={e => setWorkspaceSearchQuery(e.target.value)}
          className="pl-8"
        />
      </div>
      <DataTable
        columns={columns}
        data={filteredWorkspaces}
        isLoading={workspacesLoading}
        hidePagination
        options={{
          getRowId: row => row.id,
          enableRowSelection: true,
          onRowSelectionChange: setRowSelection,
          onSortingChange: setSorting,
          getSortedRowModel: getSortedRowModel(),
          enableMultiSort: false,
          state: { rowSelection, sorting },
        }}
      />
      <CreateWorkspaceModal
        open={createWorkspaceModalOpen}
        onOpenChange={setCreateWorkspaceModalOpen}
      />
      <WorkspaceEditModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        workspace={selectedWorkspace}
      />
      <WorkspacesActionBar
        open={selectedWorkspaces.length > 0}
        selectedWorkspaces={selectedWorkspaces}
        onClearSelection={clearSelection}
      />
    </FlexCol>
  );
};
